import { useEffect, useRef } from "react";
import type { AvatarColors, VyllageWorldElement } from "./world-engine";
import { registerVyllageElements } from "./world-engine";

let registered = false;

export function ensureVyllageElements(): void {
  if (registered) return;
  registerVyllageElements();
  registered = true;
}

type WorldHostProps = {
  className?: string;
  onReady?: (el: VyllageWorldElement) => void;
};

/** Mounts `<vyllage-world>` via DOM so React never needs custom-element JSX. */
export function VyllageWorldHost({ className, onReady }: WorldHostProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    ensureVyllageElements();
    const host = hostRef.current;
    if (!host) return;

    const el = document.createElement("vyllage-world") as VyllageWorldElement;
    el.style.cssText = "position:absolute;inset:0;width:100%;height:100%;display:block;";
    // React renders the zoom controls (SceneControls); tell the engine to skip its
    // own injected zoom chrome so there's a single, tokenized chrome system.
    el.dataset.reactZoom = "1";
    host.appendChild(el);
    onReadyRef.current?.(el);

    return () => {
      el.remove();
    };
  }, []);

  return <div ref={hostRef} className={className} style={{ position: "absolute", inset: 0 }} />;
}

type AvatarPreviewProps = {
  colors: AvatarColors;
  size?: number;
  spin?: boolean;
  className?: string;
};

export function VyllageAvatarPreview({
  colors,
  size = 100,
  spin = true,
  className,
}: AvatarPreviewProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ensureVyllageElements();
    const host = hostRef.current;
    if (!host) return;

    const el = document.createElement("vyllage-avatar");
    el.setAttribute("shirt", colors.shirt);
    el.setAttribute("hair", colors.hair);
    el.setAttribute("skin", colors.skin);
    el.setAttribute("hstyle", colors.style);
    el.setAttribute("pants", colors.pants ?? "#1F2A44");
    el.setAttribute("shoes", colors.shoes ?? "#001847");
    el.setAttribute("hat", colors.hat ?? "none");
    el.setAttribute("hatcolor", colors.hatColor ?? colors.shirt);
    el.setAttribute("glasses", colors.glasses ?? "none");
    el.setAttribute("facialhair", colors.facialHair ?? "none");
    el.setAttribute("jewelry", colors.jewelry ?? "none");
    el.setAttribute("topstyle", colors.topStyle ?? "solid");
    el.setAttribute("bottomstyle", colors.bottomStyle ?? "solid");
    el.setAttribute("shoestyle", colors.shoeStyle ?? "sneakers");
    el.setAttribute("eyecolor", colors.eyeColor ?? "#101728");
    el.setAttribute("expression", colors.expression ?? "neutral");
    el.setAttribute("backpack", colors.backpack ?? "none");
    el.setAttribute("size", String(size));
    if (spin) el.setAttribute("spin", "1");
    host.replaceChildren(el);

    return () => {
      host.replaceChildren();
    };
  }, [
    colors.shirt,
    colors.hair,
    colors.skin,
    colors.style,
    colors.pants,
    colors.shoes,
    colors.hat,
    colors.hatColor,
    colors.glasses,
    colors.facialHair,
    colors.jewelry,
    colors.topStyle,
    colors.bottomStyle,
    colors.shoeStyle,
    colors.eyeColor,
    colors.expression,
    colors.backpack,
    size,
    spin,
  ]);

  return <div ref={hostRef} className={className} />;
}
